/**
 * BalanceChartScene — Interactive balance tuning tool.
 *
 * Selectable X/Y axes, zoom/pan, draggable dots (edit mode),
 * double-click detail panel. Modifications write to GodMode/ComponentOverrides.
 */

import Phaser from 'phaser';
import { CANVAS_WIDTH, CANVAS_HEIGHT } from '@/config/GameConfig';
import { createTechButton, fillTechRect, drawTechRect, TECH } from '@/ui/TechUI';
import { SoundManager } from '@/systems/SoundManager';
import { saveEnemyTypeStatOverrides, resetEnemyTypeStatOverrides } from '@/config/GodMode';
import { resetComponentOverrides } from '@/config/ComponentOverrides';
import { EventBus, GameEvents } from '@/events/EventBus';
import {
  BalanceAxis, AXIS_LABELS, DRAGGABLE_AXES,
  computeAllUnits, setEnemyStatFromAxis, setRobotStatFromAxis, BalanceUnit,
} from '@/config/BalanceStats';
import { showDetailPanel } from '@/scenes/BalanceDetailPanel';

// ── Layout ──
const TOOLBAR_H = 58;
const MARGIN = { top: TOOLBAR_H + 12, right: 30, bottom: 50, left: 70 };
const CHART_X = MARGIN.left;
const CHART_Y = MARGIN.top;
const CHART_W = CANVAS_WIDTH - MARGIN.left - MARGIN.right;
const CHART_H = CANVAS_HEIGHT - MARGIN.top - MARGIN.bottom;

const ALL_AXES = Object.values(BalanceAxis);

export class BalanceChartScene extends Phaser.Scene {
  private returnTo = 'TitleScene';
  private xAxis: BalanceAxis = BalanceAxis.HP;
  private yAxis: BalanceAxis = BalanceAxis.DPS;
  private sizeAxis: BalanceAxis = BalanceAxis.POWER;
  private editMode = false;

  // Chart state
  private units: BalanceUnit[] = [];
  private maxX = 1;
  private maxY = 1;

  // Zoom & pan
  private zoomLevel = 1;
  private panX = 0;
  private panY = 0;
  private isPanning = false;
  private panStartX = 0;
  private panStartY = 0;

  // Containers
  private chartContainer!: Phaser.GameObjects.Container;
  private hudObjects: Phaser.GameObjects.GameObject[] = [];
  private tooltip?: Phaser.GameObjects.Container;
  private detailPanel?: Phaser.GameObjects.Container;

  // Drag state
  private dragUnit: BalanceUnit | null = null;
  private _clickedUnit: BalanceUnit | null = null;

  // Axis button refs for highlighting
  private xButtons: Map<BalanceAxis, Phaser.GameObjects.Container> = new Map();
  private yButtons: Map<BalanceAxis, Phaser.GameObjects.Container> = new Map();


  constructor() { super({ key: 'BalanceChartScene' }); }

  init(data: { returnTo?: string }): void {
    this.returnTo = data?.returnTo ?? 'TitleScene';
    this.zoomLevel = 1;
    this.panX = 0;
    this.panY = 0;
    this.editMode = false;
    this.dragUnit = null;
    this.detailPanel = undefined;
  }

  create(): void {
    // Background
    this.add.rectangle(CANVAS_WIDTH / 2, CANVAS_HEIGHT / 2, CANVAS_WIDTH, CANVAS_HEIGHT, 0x0a0a18);

    // Chart container (affected by zoom/pan)
    this.chartContainer = this.add.container(0, 0);

    this.buildToolbar();
    this.rebuildChart();
    this.setupInteraction();

    // ESC → close detail panel first, then back to returnTo
    this.input.keyboard?.on('keydown-ESC', () => {
      SoundManager.play('click');
      if (this.detailPanel) {
        this.detailPanel.destroy();
        this.detailPanel = undefined;
        EventBus.emit(GameEvents.GOD_PARAMS_CHANGED);
        this.rebuildChart();
        return;
      }
      EventBus.emit(GameEvents.GOD_PARAMS_CHANGED);
      this.scene.start(this.returnTo);
    });
  }

  // ════════════════════════════════════════
  //  TOOLBAR (HUD — not affected by zoom)
  // ════════════════════════════════════════
  private buildToolbar(): void {
    const gfx = this.add.graphics().setDepth(50);
    fillTechRect(gfx, 0, 0, CANVAS_WIDTH, TOOLBAR_H, 0x0d0d1a, 0.95);
    drawTechRect(gfx, 0, 0, CANVAS_WIDTH, TOOLBAR_H, TECH.COLOR, 0.2);
    this.hudObjects.push(gfx);

    // Back button
    const back = createTechButton(this, 50, 16, '← BACK', {
      onClick: () => { SoundManager.play('click'); EventBus.emit(GameEvents.GOD_PARAMS_CHANGED); this.scene.start(this.returnTo); },
      width: 80, height: 24, fontSize: '10px', depth: 51,
    });
    this.hudObjects.push(back);

    // Title
    const title = this.add.text(CANVAS_WIDTH / 2, 8, 'BALANCE CHART', {
      fontSize: '14px', fontFamily: 'monospace', fontStyle: 'bold', color: '#cccccc',
    }).setOrigin(0.5, 0).setDepth(51);
    this.hudObjects.push(title);

    // X-axis row
    const xLabel = this.add.text(160, 10, 'X:', {
      fontSize: '10px', fontFamily: 'monospace', color: '#8888aa',
    }).setDepth(51);
    this.hudObjects.push(xLabel);

    let bx = 180;
    for (const axis of ALL_AXES) {
      const btn = createTechButton(this, bx, 10, AXIS_LABELS[axis], {
        onClick: () => { this.xAxis = axis; this.highlightButtons(); this.rebuildChart(); },
        width: 55, height: 18, fontSize: '8px', depth: 51,
        color: axis === this.xAxis ? TECH.ACCENT : TECH.DIM,
      });
      this.xButtons.set(axis, btn);
      this.hudObjects.push(btn);
      bx += 60;
    }

    // Y-axis row
    const yLabel = this.add.text(160, 32, 'Y:', {
      fontSize: '10px', fontFamily: 'monospace', color: '#8888aa',
    }).setDepth(51);
    this.hudObjects.push(yLabel);

    let by = 180;
    for (const axis of ALL_AXES) {
      const btn = createTechButton(this, by, 32, AXIS_LABELS[axis], {
        onClick: () => { this.yAxis = axis; this.highlightButtons(); this.rebuildChart(); },
        width: 55, height: 18, fontSize: '8px', depth: 51,
        color: axis === this.yAxis ? TECH.ACCENT : TECH.DIM,
      });
      this.yButtons.set(axis, btn);
      this.hudObjects.push(btn);
      by += 60;
    }

    // Edit mode toggle
    const editBtn = createTechButton(this, CANVAS_WIDTH - 100, 10, 'EDIT', {
      onClick: () => {
        this.editMode = !this.editMode;
        SoundManager.play('click');
        this.rebuildChart();
      },
      width: 55, height: 18, fontSize: '9px', depth: 51,
      color: this.editMode ? TECH.DANGER : TECH.DIM,
    });
    this.hudObjects.push(editBtn);

    // Reset view
    const resetBtn = createTechButton(this, CANVAS_WIDTH - 100, 32, 'RESET', {
      onClick: () => { this.zoomLevel = 1; this.panX = 0; this.panY = 0; this.applyZoomPan(); },
      width: 55, height: 18, fontSize: '9px', depth: 51,
    });
    this.hudObjects.push(resetBtn);

    // Reset all stat overrides (enemies + components)
    const resetAllBtn = createTechButton(this, CANVAS_WIDTH - 170, 32, 'RESET STATS', {
      onClick: () => {
        resetComponentOverrides();
        resetEnemyTypeStatOverrides();
        SoundManager.play('click');
        this.rebuildChart();
      },
      width: 65, height: 18, fontSize: '8px', depth: 51,
      color: TECH.DANGER,
    });
    this.hudObjects.push(resetAllBtn);
  }

  private highlightButtons(): void {
    // We can't easily restyle createTechButton, so we rebuild the chart which refreshes visuals
    // The active axis gets a visual cue via the chart subtitle
  }

  // ════════════════════════════════════════
  //  CHART REBUILD
  // ════════════════════════════════════════
  private rebuildChart(): void {
    // Clear old chart content
    this.chartContainer.removeAll(true);
    this.hideTooltip();

    this.units = computeAllUnits();

    // Axis ranges
    const xVals = this.units.map(u => u.stats[this.xAxis]);
    const yVals = this.units.map(u => u.stats[this.yAxis]);
    this.maxX = Math.max(1, Math.ceil(Math.max(...xVals) * 1.15));
    this.maxY = Math.max(1, Math.ceil(Math.max(...yVals) * 1.15));

    const gfx = new Phaser.GameObjects.Graphics(this);
    this.chartContainer.add(gfx);

    // Chart background
    fillTechRect(gfx, CHART_X - 5, CHART_Y - 5, CHART_W + 10, CHART_H + 10, 0x111122, 0.8);
    drawTechRect(gfx, CHART_X - 5, CHART_Y - 5, CHART_W + 10, CHART_H + 10, TECH.COLOR, 0.15);

    // Grid
    this.drawGrid(gfx);

    // Iso-power lines
    this.drawIsoPowerLines(gfx);

    // Axis labels
    const xLbl = this.make.text({
      x: CANVAS_WIDTH / 2, y: CANVAS_HEIGHT - 8, text: AXIS_LABELS[this.xAxis],
      style: { fontSize: '11px', fontFamily: 'monospace', color: '#8888aa' },
      add: false,
    }).setOrigin(0.5);
    this.chartContainer.add(xLbl);

    const yLbl = this.make.text({
      x: 12, y: CANVAS_HEIGHT / 2, text: AXIS_LABELS[this.yAxis],
      style: { fontSize: '11px', fontFamily: 'monospace', color: '#8888aa' },
      add: false,
    }).setOrigin(0.5).setRotation(-Math.PI / 2);
    this.chartContainer.add(yLbl);

    // Dots
    for (const u of this.units) {
      this.drawDot(u, gfx);
    }

    // Legend
    this.drawLegend(gfx);

    this.applyZoomPan();
  }

  // ── Coordinate mapping ──
  private toScreenX(val: number): number {
    return CHART_X + (val / this.maxX) * CHART_W;
  }
  private toScreenY(val: number): number {
    return CHART_Y + CHART_H - (val / this.maxY) * CHART_H;
  }
  private fromScreenX(sx: number): number {
    return ((sx - CHART_X) / CHART_W) * this.maxX;
  }
  private fromScreenY(sy: number): number {
    return ((CHART_Y + CHART_H - sy) / CHART_H) * this.maxY;
  }

  // ── Draw single dot ──
  private drawDot(u: BalanceUnit, gfx: Phaser.GameObjects.Graphics): void {
    const x = this.toScreenX(u.stats[this.xAxis]);
    const y = this.toScreenY(u.stats[this.yAxis]);

    // Size from sizeAxis
    const sizeVals = this.units.map(u2 => u2.stats[this.sizeAxis]);
    const maxSize = Math.max(1, ...sizeVals);
    const normSize = u.stats[this.sizeAxis] / maxSize;
    const radius = Math.max(5, Math.min(20, 5 + normSize * 15));

    const dotColor = u.isEnemy ? 0xff4444 : 0x4488ff;

    // Modified halo
    if (u.isModified) {
      gfx.lineStyle(2, 0xff8800, 0.6);
      gfx.strokeCircle(x, y, radius + 5);
    }

    // Main dot
    gfx.fillStyle(dotColor, 0.7);
    gfx.fillCircle(x, y, radius);
    gfx.lineStyle(1.5, u.color, 0.9);
    gfx.strokeCircle(x, y, radius);

    // Flying ring
    if (u.isFlying) {
      gfx.lineStyle(1, 0xffffff, 0.35);
      gfx.strokeCircle(x, y, radius + 3);
    }

    // Label
    const label = this.make.text({
      x, y: y - radius - 5,
      text: u.name,
      style: { fontSize: '9px', fontFamily: 'monospace', fontStyle: 'bold', color: u.isEnemy ? '#ff8888' : '#88aaff' },
      add: false,
    }).setOrigin(0.5, 1);
    this.chartContainer.add(label);

    // Hit zone — draggable on any axis that supports it
    const canDrag = DRAGGABLE_AXES.has(this.xAxis) || DRAGGABLE_AXES.has(this.yAxis);
    const hitSize = Math.max(radius * 2.5, 24);
    const zone = new Phaser.GameObjects.Zone(this, x, y, hitSize, hitSize).setInteractive({
      hitArea: new Phaser.Geom.Rectangle(-hitSize / 2, -hitSize / 2, hitSize, hitSize),
      hitAreaCallback: Phaser.Geom.Rectangle.Contains,
      draggable: canDrag,
      cursor: canDrag ? 'grab' : 'pointer',
    });
    this.chartContainer.add(zone);

    // Hover tooltip
    zone.on('pointerover', () => this.showTooltip(x, y, u));
    zone.on('pointerout', () => this.hideTooltip());

    // Click → open detail/edit panel (only if not dragging)
    zone.on('pointerdown', (ptr: Phaser.Input.Pointer) => {
      if (ptr.rightButtonDown() || ptr.middleButtonDown()) return;
      this._clickedUnit = u;
    });
    zone.on('pointerup', () => {
      // Only open detail if we didn't drag
      if (this._clickedUnit === u && this.dragUnit !== u) {
        this.openDetailPanel(u);
      }
      this._clickedUnit = null;
    });

    // Drag — works for both enemies and robot templates
    if (canDrag) {
      zone.on('drag', (_pointer: Phaser.Input.Pointer, dragX: number, dragY: number) => {
        this.dragUnit = u;
        // dragX/dragY are already in container-local space (Phaser container drag)
        const newXVal = Math.max(0.1, this.fromScreenX(dragX));
        const newYVal = Math.max(0.1, this.fromScreenY(dragY));

        // When both axes are the same, only apply X to avoid double-set corruption
        const applyX = DRAGGABLE_AXES.has(this.xAxis);
        const applyY = DRAGGABLE_AXES.has(this.yAxis) && this.yAxis !== this.xAxis;

        if (u.isEnemy && u.enemyType) {
          if (applyX) setEnemyStatFromAxis(u.enemyType, this.xAxis, newXVal);
          if (applyY) setEnemyStatFromAxis(u.enemyType, this.yAxis, newYVal);
        } else if (!u.isEnemy && u.templateIndex !== undefined) {
          if (applyX) setRobotStatFromAxis(u.templateIndex, this.xAxis, newXVal);
          if (applyY) setRobotStatFromAxis(u.templateIndex, this.yAxis, newYVal);
        }
      });
      zone.on('dragend', () => {
        if (u.isEnemy) saveEnemyTypeStatOverrides();
        this.dragUnit = null;
        this.rebuildChart();
      });
    }
  }

  // ── Grid ──
  private drawGrid(gfx: Phaser.GameObjects.Graphics): void {
    const xStep = this.niceStep(this.maxX, 6);
    const yStep = this.niceStep(this.maxY, 6);

    for (let v = xStep; v < this.maxX; v += xStep) {
      const sx = this.toScreenX(v);
      gfx.lineStyle(1, 0x222244, 0.4);
      gfx.lineBetween(sx, CHART_Y, sx, CHART_Y + CHART_H);
      const txt = this.make.text({
        x: sx, y: CHART_Y + CHART_H + 6,
        text: this.formatAxisVal(v),
        style: { fontSize: '8px', fontFamily: 'monospace', color: '#555577' },
        add: false,
      }).setOrigin(0.5, 0);
      this.chartContainer.add(txt);
    }

    for (let v = yStep; v < this.maxY; v += yStep) {
      const sy = this.toScreenY(v);
      gfx.lineStyle(1, 0x222244, 0.4);
      gfx.lineBetween(CHART_X, sy, CHART_X + CHART_W, sy);
      const txt = this.make.text({
        x: CHART_X - 6, y: sy,
        text: this.formatAxisVal(v),
        style: { fontSize: '8px', fontFamily: 'monospace', color: '#555577' },
        add: false,
      }).setOrigin(1, 0.5);
      this.chartContainer.add(txt);
    }

    // Axes
    gfx.lineStyle(1.5, TECH.COLOR, 0.4);
    gfx.lineBetween(CHART_X, CHART_Y, CHART_X, CHART_Y + CHART_H);
    gfx.lineBetween(CHART_X, CHART_Y + CHART_H, CHART_X + CHART_W, CHART_Y + CHART_H);
  }

  // ── Iso-power lines ──
  private drawIsoPowerLines(gfx: Phaser.GameObjects.Graphics): void {
    const powers = this.units.map(u => u.stats[BalanceAxis.POWER]).sort((a, b) => a - b);
    if (powers.length < 3) return;
    const levels = [
      powers[Math.floor(powers.length * 0.25)],
      powers[Math.floor(powers.length * 0.50)],
      powers[Math.floor(powers.length * 0.75)],
    ];

    for (const p of levels) {
      const k = p * p;
      gfx.lineStyle(1, 0x444466, 0.2);
      gfx.beginPath();
      let started = false;
      for (let xv = 1; xv <= this.maxX; xv += this.maxX / 200) {
        const yv = k / xv;
        if (yv < 0 || yv > this.maxY) continue;
        const sx = this.toScreenX(xv);
        const sy = this.toScreenY(yv);
        if (!started) { gfx.moveTo(sx, sy); started = true; }
        else gfx.lineTo(sx, sy);
      }
      gfx.strokePath();
    }
  }

  // ── Legend ──
  private drawLegend(gfx: Phaser.GameObjects.Graphics): void {
    const lx = CHART_X + CHART_W - 160;
    const ly = CHART_Y + CHART_H - 86;

    fillTechRect(gfx, lx, ly, 155, 78, 0x111122, 0.85);
    drawTechRect(gfx, lx, ly, 155, 78, TECH.COLOR, 0.2);

    gfx.fillStyle(0x4488ff, 0.8); gfx.fillCircle(lx + 12, ly + 15, 5);
    const t1 = this.make.text({ x: lx + 24, y: ly + 15, text: 'Robot', style: { fontSize: '9px', fontFamily: 'monospace', color: '#88aaff' }, add: false }).setOrigin(0, 0.5);
    this.chartContainer.add(t1);

    gfx.fillStyle(0xff4444, 0.8); gfx.fillCircle(lx + 12, ly + 32, 5);
    const t2 = this.make.text({ x: lx + 24, y: ly + 32, text: 'Enemy', style: { fontSize: '9px', fontFamily: 'monospace', color: '#ff8888' }, add: false }).setOrigin(0, 0.5);
    this.chartContainer.add(t2);

    gfx.lineStyle(1.5, 0xff8800, 0.7); gfx.strokeCircle(lx + 12, ly + 49, 7);
    const t3 = this.make.text({ x: lx + 24, y: ly + 49, text: 'Modified', style: { fontSize: '9px', fontFamily: 'monospace', color: '#ff8800' }, add: false }).setOrigin(0, 0.5);
    this.chartContainer.add(t3);

    gfx.lineStyle(1, 0xffffff, 0.3); gfx.strokeCircle(lx + 12, ly + 65, 5); gfx.strokeCircle(lx + 12, ly + 65, 8);
    const t4 = this.make.text({ x: lx + 24, y: ly + 65, text: 'Flying', style: { fontSize: '9px', fontFamily: 'monospace', color: '#aaaaaa' }, add: false }).setOrigin(0, 0.5);
    this.chartContainer.add(t4);
  }

  // ════════════════════════════════════════
  //  ZOOM & PAN
  // ════════════════════════════════════════
  private setupInteraction(): void {
    // Zoom
    this.input.on('wheel', (ptr: Phaser.Input.Pointer, _gos: any, _dx: number, dy: number) => {
      if (this.detailPanel) return;
      const oldZoom = this.zoomLevel;
      const step = dy < 0 ? 0.15 : -0.15;
      this.zoomLevel = Phaser.Math.Clamp(this.zoomLevel + step, 1, 4);
      // Zoom centered on mouse position
      const ratio = this.zoomLevel / oldZoom;
      this.panX = ptr.x - ratio * (ptr.x - this.panX);
      this.panY = ptr.y - ratio * (ptr.y - this.panY);
      // Clamp pan so chart doesn't drift off-screen at zoom=1
      this.clampPan();
      this.applyZoomPan();
    });

    // Pan (right/middle-click drag, or left-click on background when zoomed)
    this.input.on('pointerdown', (ptr: Phaser.Input.Pointer) => {
      if (ptr.rightButtonDown() || ptr.middleButtonDown()) {
        this.isPanning = true;
        this.panStartX = ptr.x - this.panX;
        this.panStartY = ptr.y - this.panY;
      } else if (this.zoomLevel > 1 && this.input.hitTestPointer(ptr).length === 0) {
        // Left-click on empty area when zoomed → pan
        this.isPanning = true;
        this.panStartX = ptr.x - this.panX;
        this.panStartY = ptr.y - this.panY;
      }
    });
    this.input.on('pointermove', (ptr: Phaser.Input.Pointer) => {
      if (this.isPanning && ptr.isDown) {
        this.panX = ptr.x - this.panStartX;
        this.panY = ptr.y - this.panStartY;
        this.clampPan();
        this.applyZoomPan();
      }
    });
    this.input.on('pointerup', () => { this.isPanning = false; });

    // Disable context menu
    this.input.mouse?.disableContextMenu();
  }

  /** Prevent panning beyond the chart boundaries */
  private clampPan(): void {
    const scaledW = CANVAS_WIDTH * this.zoomLevel;
    const scaledH = CANVAS_HEIGHT * this.zoomLevel;
    // At zoom=1, pan must be 0. At zoom>1, allow negative pan up to the overflow.
    this.panX = Phaser.Math.Clamp(this.panX, -(scaledW - CANVAS_WIDTH), 0);
    this.panY = Phaser.Math.Clamp(this.panY, -(scaledH - CANVAS_HEIGHT), 0);
  }

  private applyZoomPan(): void {
    this.chartContainer.setScale(this.zoomLevel);
    this.chartContainer.setPosition(this.panX, this.panY);
  }

  // ════════════════════════════════════════
  //  DETAIL PANEL
  // ════════════════════════════════════════
  private openDetailPanel(u: BalanceUnit): void {
    if (this.detailPanel) { this.detailPanel.destroy(); this.detailPanel = undefined; }
    this.detailPanel = showDetailPanel(this, u, () => {
      this.detailPanel = undefined;
      EventBus.emit(GameEvents.GOD_PARAMS_CHANGED);
      this.rebuildChart();
    });
  }

  // ════════════════════════════════════════
  //  TOOLTIP
  // ════════════════════════════════════════
  private showTooltip(x: number, y: number, u: BalanceUnit): void {
    this.hideTooltip();
    const lines = [
      u.name + (u.isFlying ? ' ✈' : ''),
      ...ALL_AXES.map(a => `${AXIS_LABELS[a]}: ${this.formatAxisVal(u.stats[a])}`),
    ];
    const textObj = this.make.text({
      x: 0, y: 0, text: lines.join('\n'),
      style: { fontSize: '9px', fontFamily: 'monospace', color: '#ffffff', lineSpacing: 2 },
      add: false,
    }).setOrigin(0.5, 1);

    const pad = 5;
    const tw = textObj.width + pad * 2;
    const th = textObj.height + pad * 2;

    const bg = new Phaser.GameObjects.Graphics(this);
    fillTechRect(bg, -tw / 2, -textObj.height - pad, tw, th, 0x1a1a2e, 0.95);
    drawTechRect(bg, -tw / 2, -textObj.height - pad, tw, th, u.isEnemy ? 0xff4444 : 0x4488ff, 0.7);

    const container = this.add.container(x, y - 25).setDepth(200);
    container.add([bg, textObj]);
    this.tooltip = container;
  }

  private hideTooltip(): void {
    if (this.tooltip) { this.tooltip.destroy(); this.tooltip = undefined; }
  }

  // ════════════════════════════════════════
  //  UTILITIES
  // ════════════════════════════════════════
  private formatAxisVal(v: number): string {
    if (v >= 1000) return (v / 1000).toFixed(1) + 'k';
    if (v >= 100) return Math.round(v).toString();
    if (v >= 10) return v.toFixed(1);
    return v.toFixed(2);
  }

  private niceStep(max: number, targetLines: number): number {
    if (max <= 0) return 1;
    const raw = max / targetLines;
    const mag = Math.pow(10, Math.floor(Math.log10(raw)));
    const norm = raw / mag;
    if (norm <= 1.5) return mag;
    if (norm <= 3.5) return 2.5 * mag;
    if (norm <= 7.5) return 5 * mag;
    return 10 * mag;
  }
}
